/** Add-ons mix sliders. 100% = 0 dB; 45% = today's med (−16 dB). */

export const DEFAULT_MUSIC_VOLUME = 45;
export const DEFAULT_REEL_AUDIO_VOLUME = 100;
export const DEFAULT_VOICE_VOLUME = 100;

const VOLUME_DB_K = -16 / Math.log10(0.45);

export function clampVolumePercent(raw: unknown, fallback = DEFAULT_MUSIC_VOLUME): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(100, Math.round(n)));
}

export function gainDbToVolumePercent(db: number): number {
  if (db <= -60) return 0;
  if (db >= 0) return 100;
  return Math.max(0, Math.min(100, Math.round(100 * 10 ** (db / VOLUME_DB_K))));
}

/** Same curve as backend `volume_percent_to_gain_db`. 0% = mute. */
export function volumePercentToGainDb(
  pct: unknown,
  fallback = DEFAULT_MUSIC_VOLUME
): number {
  const n = clampVolumePercent(pct, fallback);
  if (n <= 0) return -120;
  if (n >= 100) return 0;
  return VOLUME_DB_K * Math.log10(n / 100);
}

/** HTMLMediaElement.volume (0–1) from a mix slider percent. */
export function volumePercentToLinear(
  pct: unknown,
  fallback = DEFAULT_MUSIC_VOLUME
): number {
  const n = clampVolumePercent(pct, fallback);
  if (n <= 0) return 0;
  return Math.max(0, Math.min(1, 10 ** (volumePercentToGainDb(n) / 20)));
}

/** Map stored low/med/high onto the % that reproduces that dB (no remux). */
export function legacyLevelToMusicVolume(level: unknown): number {
  const key = String(level || '').trim().toLowerCase();
  if (key === 'low') return gainDbToVolumePercent(-22);
  if (key === 'high') return gainDbToVolumePercent(-10);
  return DEFAULT_MUSIC_VOLUME;
}

export function initialMusicVolume(job: {
  music_volume?: number | null;
  music_addon_level?: string | null;
}): number {
  if (typeof job.music_volume === 'number') {
    return clampVolumePercent(job.music_volume);
  }
  if (job.music_addon_level) {
    return legacyLevelToMusicVolume(job.music_addon_level);
  }
  return DEFAULT_MUSIC_VOLUME;
}

export function initialReelAudioVolume(job: {
  reel_audio_volume?: number | null;
}): number {
  if (typeof job.reel_audio_volume === 'number') {
    return clampVolumePercent(job.reel_audio_volume, DEFAULT_REEL_AUDIO_VOLUME);
  }
  return DEFAULT_REEL_AUDIO_VOLUME;
}

export function initialVoiceVolume(job: {
  voice_volume?: number | null;
}): number {
  if (typeof job.voice_volume === 'number') {
    return clampVolumePercent(job.voice_volume, DEFAULT_VOICE_VOLUME);
  }
  return DEFAULT_VOICE_VOLUME;
}

export function isMixVersion(version: unknown): boolean {
  const value = String(version || '').trim().toLowerCase();
  return value === 'mix' || value === 'music';
}
